import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { HelpLabel } from '@/components/common/help/HelpLabel'
import { HelpText } from '@/components/common/help/HelpText'
import type { ActionModuleSchedulingStrategyState } from '@/lib/action-modules/registry/types'
import { schedulingStrategies } from '@/lib/action-modules/registry'
import { helpKey } from '@/lib/action-modules/help'
import type { SchedulingStrategyType } from '@/api/model'

interface Props {
  value: ActionModuleSchedulingStrategyState
  onChange: (value: ActionModuleSchedulingStrategyState) => void
}

export default function SchedulingStrategyTab({ value, onChange }: Props) {
  const { t } = useTranslation('action-modules-wizard')
  const definition = useMemo(
    () => schedulingStrategies.find(i => i.type === value.selectedSchedulingStrategyType),
    [value.selectedSchedulingStrategyType],
  )

  const onChangeType = (newType: SchedulingStrategyType) => {
    const def = schedulingStrategies.find(i => i.type === newType)
    onChange({
      selectedSchedulingStrategyType: newType,
      schedulingStrategy: { ...(def?.defaultValue ?? {}), type: newType },
    })
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <h3 className="text-sm font-semibold">{t('tabScheduling.common')}</h3>
      <div className="flex max-w-md flex-col gap-2">
        <HelpLabel helpKey={helpKey('schedulingStrategyType')}>{t('tabScheduling.schedulingStrategyType')}</HelpLabel>
        <Select
          value={value.selectedSchedulingStrategyType ?? undefined}
          onValueChange={v => onChangeType(v as SchedulingStrategyType)}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t('tabScheduling.schedulingStrategyType')} />
          </SelectTrigger>
          <SelectContent>
            {schedulingStrategies.map(s => (
              <SelectItem key={s.type} value={s.type}>
                {t(s.nameKey)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {definition?.helpKey && <HelpText helpKey={definition.helpKey} />}
      </div>

      {definition && (
        <div className="pt-1">
          {definition.editor({
            value: value.schedulingStrategy,
            onChanged: v => onChange({ ...value, schedulingStrategy: v }),
          })}
        </div>
      )}
    </div>
  )
}
